import { useState } from "react";
import { Button } from "@/components/ui/button";
import { FileText, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";

interface DocumentsDropdownProps {
  prescriptionId: string;
  prescriptionDriveUrl?: string | null;
  prescriptionDocId?: string | null;
  orderSummaryDriveUrl?: string | null;
  forceShow?: boolean;
}

export function DocumentsDropdown({
  prescriptionId,
  prescriptionDriveUrl,
  prescriptionDocId,
  orderSummaryDriveUrl,
  forceShow = false,
}: DocumentsDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const resolvePrescriptionUrl = (
    driveUrl?: string | null,
    docIdOrUrl?: string | null,
  ): string | null => {
    if (driveUrl) return driveUrl;
    if (!docIdOrUrl) return null;

    const value = docIdOrUrl.trim();
    if (!value) return null;

    // If prescription_id already contains a Google Docs URL, use it directly.
    if (/^https?:\/\//i.test(value)) {
      return value;
    }

    // If it looks like a Google Doc ID, build the URL.
    if (/^[a-zA-Z0-9_-]{20,}$/.test(value)) {
      return `https://docs.google.com/document/d/${value}`;
    }

    return null;
  };

  const resolvedPrescriptionUrl = resolvePrescriptionUrl(
    prescriptionDriveUrl,
    prescriptionDocId,
  );

  if (!resolvedPrescriptionUrl && !orderSummaryDriveUrl && !forceShow) {
    return null;
  }

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        className="h-8 px-2"
        onClick={() => setIsOpen(!isOpen)}
      >
        <FileText className="h-3 w-3 mr-1" />
        {isOpen ? (
          <ChevronUp className="h-3 w-3" />
        ) : (
          <ChevronDown className="h-3 w-3" />
        )}
      </Button>
      {isOpen && (
        <div className="absolute right-0 top-full mt-1 z-10 bg-white border rounded-md shadow-lg p-2 min-w-[200px]">
          <div className="space-y-2">
            {resolvedPrescriptionUrl && (
              <a
                href={resolvedPrescriptionUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-xs text-blue-600 hover:underline"
              >
                <ExternalLink className="h-3 w-3" />
                <span>Prescription Document</span>
              </a>
            )}
            {orderSummaryDriveUrl && (
              <a
                href={orderSummaryDriveUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-xs text-blue-600 hover:underline"
              >
                <ExternalLink className="h-3 w-3" />
                <span>Order Summary</span>
              </a>
            )}
            {!resolvedPrescriptionUrl && !orderSummaryDriveUrl && forceShow && (
              <div className="text-xs text-muted-foreground">
                Document link not available yet.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
